import React, {
	useEffect,
	useState
} from 'react';
import {
	JobUploadFile,
	JobUploadFileListWithoutManifest
} from '../../store/selectors/jobsSelectors';
import { JobAudioUploadProgressBar } from './JobAudioUploadProgressBar';
import { EngagementExpandedSummary } from '../../models/dto/engagement';
import './JobDetailsFileListWithoutManifest.scss';
import JobDetailsFileStatus from './JobDetailsFileStatus';
import FormattedDate from '../FormattedDate';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/actions/appDispatch';
import { updateJobCallStatuses } from '../../store/actions/jobsActions';
import TethrCallLink from './TethrCallLink';
import orderBy from 'lodash/orderBy';
import { SortDirection } from '../../models/sortDirection';
import SortIndicator from '../SortIndicator';

const pollingInterval = 1000 * 30;

export default function JobDetailsFileListWithoutManifest(props: { jobId: number; fileList: JobUploadFileListWithoutManifest; engagement: EngagementExpandedSummary; }) {
	const callCount = props.fileList.files.length.toLocaleString();
	const dispatch = useDispatch<AppDispatch>();
	const [sortTarget, setSortTarget] = useState<SortTarget>('date');
	const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
	const sortedFiles = getSortedFiles(props.fileList.files, sortTarget, sortDirection);
	
	useEffect(() => {
		const interval = setInterval(() => {
			dispatch(updateJobCallStatuses(props.jobId));
		}, pollingInterval);
		
		return () => {
			clearInterval(interval);
		};
	}, [props.jobId]);
	
	return (
		<React.Fragment>
			<div className="flex flex-align-center mb-3">
				<div className="flex-item-static">
					<h3>My calls ({callCount})</h3>
				</div>
				{props.engagement.showTethrAppIntegration && <div className="flex-item-static job-details-file-list-without-manifest__email-msg">You will receieve an email once your calls are ready to view</div>}
			</div>

			<div className="boxed-list__container boxed-list__container_with-header">
				<div className="boxed-list__header boxed-list__header_with-list-item-alignment-padding">
					<div className="flex flex-align-center full-height">
						<div className="job-details-file-list-without-manifest__filename-flex-item">
							<div className="flex flex-align-center">
								<div className="flex-item-static"><h5 className="cursor-pointer" onClick={() => updateSort('name')}>File name</h5></div>
								<div className="flex-item-static"><div className="job-details-file-list-without-manifest__sort-indicator"><SortIndicator sortClicked={() => updateSort('name')} sortDirection={sortTarget === 'name' ? sortDirection : null} /></div></div>
							</div>
						</div>
						<div className="job-details-file-list-without-manifest__date-flex-item">
							<div className="flex flex-align-center">
								<div className="flex-item-static"><h5 className="cursor-pointer" onClick={() => updateSort('date')}>Date</h5></div>
								<div className="flex-item-static"><div className="job-details-file-list-without-manifest__sort-indicator"><SortIndicator sortClicked={() => updateSort('date')} sortDirection={sortTarget === 'date' ? sortDirection : null} /></div></div>
							</div>
						</div>
						<div className="job-details-file-list-without-manifest__progress-flex-item"></div>
						<div className="job-details-file-list-without-manifest__status-flex-item">
							<div className="flex flex-align-center">
								<div className="flex-item-static"><h5 className="cursor-pointer" onClick={() => updateSort('status')}>Status</h5></div>
								<div className="flex-item-static"><div className="job-details-file-list-without-manifest__sort-indicator"><SortIndicator sortClicked={() => updateSort('status')} sortDirection={sortTarget === 'status' ? sortDirection : null} /></div></div>
							</div>
						</div>
					</div>
				</div>

				<div className="boxed-list__item-container">
					{sortedFiles.map((f) => (
						<div key={getKey(f)} className="boxed-list__item boxed-list__item_small-text">
							<div className="flex flex-align-center">
								<div className="job-details-file-list-without-manifest__filename-flex-item">
									<div className="job-details-file-list-without-manifest__filename">{renderFilename(f)}</div>
								</div>
								<div className="job-details-file-list-without-manifest__date-flex-item">{f.type === 'uploaded' ? <FormattedDate date={f.details.startTime} dateType="dateOnly" /> : '-'}</div>
								<div className="job-details-file-list-without-manifest__progress-flex-item">
									{f.type === 'uploading' && (f.details.status === 'uploading' || f.details.status === 'finishing') && (
										<JobAudioUploadProgressBar jobId={props.jobId} callIdentifier={f.details.callIdentifier} isComplete={f.details.status === 'finishing'} />
									)}
								</div>
								<div className="job-details-file-list-without-manifest__status-flex-item">
									<JobDetailsFileStatus file={f} engagement={props.engagement}/>
								</div>
							</div>
						</div>
					))}

					{!sortedFiles.length &&
						<div className="boxed-list__item boxed-item__item_empty">No files</div>
					}
				</div>
			</div>
		</React.Fragment>
	);
	
	function updateSort(target: SortTarget): void {
		if (target === sortTarget) {
			setSortDirection(prevState => prevState === 'asc' ? 'desc' : 'asc');
		} else {
			switch (target) {
				case 'name':
					setSortTarget('name');
					setSortDirection('asc');
					break;
				case 'status':
					setSortTarget('status');
					setSortDirection('asc');
					break;
				case 'date':
					setSortTarget('date');
					setSortDirection('desc');
					break;
			}
		}
	}
	
	function renderFilename(file: JobUploadFile) {
		if (file.type === 'uploaded') {
			if (props.engagement.showTethrAppIntegration) {
				return <TethrCallLink call={file.details} tethrUrl={props.engagement.tethrUrl} hasLinkText={file.details.fileName} noLinkText={file.details.fileName} />;
			} else {
				return <div>{file.details.fileName}</div>;
			}
		} else {
			return <div>{file.details.filename}</div>;
		}
	}
}

function getKey(file: JobUploadFile): string {
	if (file.type === 'uploaded') {
		return `callId_${file.details.id}`;
	} else {
		return `uploadId_${file.details.uploadId}`;
	}
}

function getSortedFiles(files: JobUploadFile[], target: SortTarget, direction: SortDirection): JobUploadFile[] {
	const order: 'asc' | 'desc' = direction;
	
	switch (target) {
		case 'name':
			return orderBy(files, [getFileName], [order]);
		case 'date':
			return orderBy(files, [f => f.type === 'uploading' ? 1 : f.type === 'failure' ? 2 : 3, getFileTime, getFileUploadOrder], ['asc', order, 'asc']);
		case 'status':
			return orderBy(files, [getFileStatusSort, getFileUploadOrder, getFileTime], [order, 'asc', 'desc']);
		default:
			return files;
	}
}

function getFileName(file: JobUploadFile): string {
	return (file.type === 'uploaded' ? file.details.fileName : file.details.filename) ?? '';
}

function getFileTime(file: JobUploadFile): number {
	if (file.type === 'uploaded') {
		const startTime = (new Date(file.details.startTime)).getTime();
		return isNaN(startTime) ? 0 : startTime;
	} else {
		return 0;
	}
}

function getFileUploadOrder(file: JobUploadFile): number {
	return file.type === 'uploaded' ? 0 : file.details.uploadOrder;
}

function getFileStatusSort(file: JobUploadFile): number {
	if (file.type === 'uploading') {
		switch (file.details.status) {
			case 'queued':
				return 1;
			case 'uploading':
			case 'finishing':
				return 2;
		}
	} else if (file.type === 'failure'){
		return 6;
	} else {
		switch (file.details.statusId) {
			case 'NotStarted':
			case 'Uploading':
				return 3;
			case 'Processing':
				return 4;
			case 'Complete':
				return 5;
			case 'Rejected':
				return 7;
			default:
				return 8;
		}
	}
}

type SortTarget = 'name' | 'date' | 'status';
